export type CandidateStatus = 'new' | 'screening' | 'interview' | 'offer' | 'hired' | 'rejected'

export interface Vacancy {
  id: string | number
  title: string
  country: string
  description: string
  job_profile: { id: string | number; title: string } | string | null
  created_at: string
  updated_at: string
}
export type InterviewType = 'phone' | 'video' | 'onsite'
export type InterviewStatus = 'scheduled' | 'completed' | 'cancelled'

export interface Candidate {
  id: string
  first_name: string
  last_name: string
  email: string
  phone: string
  country: string
  source: string
  status: CandidateStatus
  applied_at: string
  resume_url: string
  notes: string
  vacancy: { id: string; title: string; country: string } | null
  created_at: string
  updated_at: string
}

export interface Application {
  id: string
  candidate: Candidate | string
  vacancy: { id: string; title: string } | string | null
  status: CandidateStatus
  interview_stage: number | null
  stage: string
  applied_at: string
  updated_at: string
}

export interface Interview {
  id: string
  application: Application | string
  interviewer: { id: string; first_name: string; last_name: string; profile_picture?: string } | string
  scheduled_at: string
  duration_minutes: number
  type: InterviewType
  status: InterviewStatus
  notes: string
  rating: number | null
}

export interface VacancyOverviewCandidate {
  id: string
  first_name: string
  last_name: string
  email: string
  phone: string
  country: string
  source: string
  status: CandidateStatus
  stage: string
  interview_stage: number | null
  interview_count: number
  applied_at: string
}

export interface VacancyOverview {
  id: string | null
  title: string
  country: string
  job_profile: { id: string; title: string } | null
  candidate_count: number
  candidates: VacancyOverviewCandidate[]
}

export interface RecruitmentReport {
  time_to_hire: number
  source_breakdown: { source: string; count: number; hired: number }[]
  pipeline_summary: { status: CandidateStatus; count: number }[]
  funnel_data: { stage: string; count: number }[]
  trend_data: { month: string; applications: number; hires: number }[]
}

export interface PaginatedResponse<T> {
  data: T[]
  count: number
  has_next: boolean
  max_page: number
}

export const CANDIDATE_STATUS_LABELS: Record<CandidateStatus, string> = {
  new: 'New',
  screening: 'Screening',
  interview: 'Interview',
  offer: 'Offer',
  hired: 'Hired',
  rejected: 'Rejected',
}

export const CANDIDATE_STATUS_COLORS: Record<CandidateStatus, { bg: string; text: string }> = {
  new: { bg: 'solid.50', text: 'solid.700' },
  screening: { bg: 'warning.0', text: 'warning.100' },
  interview: { bg: 'aqua.0', text: 'aqua.100' },
  offer: { bg: 'primary.0', text: 'primary.100' },
  hired: { bg: 'success.0', text: 'success.100' },
  rejected: { bg: 'error.0', text: 'error.100' },
}

export const INTERVIEW_TYPE_LABELS: Record<InterviewType, string> = {
  phone: 'Phone',
  video: 'Video',
  onsite: 'On-site',
}

export const PIPELINE_COLUMNS: CandidateStatus[] = ['new', 'screening', 'interview', 'offer', 'hired', 'rejected']
